import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { downloadInvoicePdf, printInvoicePdf } from '../../utils/invoicePdf'
import RhTemplateMenu from '../RhTemplateMenu/RhTemplateMenu'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { getInvoicesByUser } from '../../actions/invoiceActions'
import Spinner from '../Spinner/Spinner'
import { formatTND } from '../../utils/formatTND'
import styles from './Invoices.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'sent', label: 'Envoyées' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échecs' },
]

const statusLabel = {
  sent: { text: 'Envoyée', cls: 'sent' },
  failed: { text: 'Échec', cls: 'failed' },
  pending: { text: 'En attente', cls: 'pending' },
}

const formatPeriod = (month, year) => {
  if (!month || !year) return '—'
  return `${MONTHS[month]} ${year}`
}

const getAmounts = (inv) => {
  const honoraires = Number(inv.honoraires ?? inv.subTotal ?? 0)
  const netAPayer = Number(inv.netAPayer ?? honoraires + (inv.droitTimbre ?? 1))
  return { honoraires, netAPayer }
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const now = new Date()
const CURRENT_MONTH = now.getMonth() + 1
const CURRENT_YEAR = now.getFullYear()

const getInvoicePeriodKey = (inv) => {
  if (!inv.periodYear || !inv.periodMonth) return null
  return inv.periodYear * 12 + inv.periodMonth
}

const parseMonthValue = (value) => {
  if (!value) return null
  const [year, month] = value.split('-').map(Number)
  if (!year || !month) return null
  return year * 12 + month
}

const toMonthInput = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}`

const Invoices = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  useRhGuard()

  const profile = JSON.parse(localStorage.getItem('profile'))
  const token = profile?.token
  const userId = profile?.result?._id || profile?.result?.googleId

  const rowsFromStore = useSelector((state) => state.invoices.invoices)
  const isLoading = useSelector((state) => state.invoices.isLoading)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [monthFilter, setMonthFilter] = useState(String(CURRENT_MONTH))
  const [yearFilter, setYearFilter] = useState(String(CURRENT_YEAR))
  const [useCustomRange, setUseCustomRange] = useState(false)
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [pdfBusyInvoiceId, setPdfBusyInvoiceId] = useState(null)

  useEffect(() => {
    if (!token) {
      history.push('/')
      return
    }
    dispatch(getInvoicesByUser({ search: userId }))
  }, [dispatch, location, token, userId, history])

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter, monthFilter, yearFilter, useCustomRange, rangeStart, rangeEnd, pageSize])

  const resetFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setMonthFilter(String(CURRENT_MONTH))
    setYearFilter(String(CURRENT_YEAR))
    setUseCustomRange(false)
    setRangeStart('')
    setRangeEnd('')
    setPageSize(10)
  }

  const enableCustomRange = () => {
    setUseCustomRange(true)
    if (monthFilter !== 'all' && yearFilter !== 'all') {
      const value = toMonthInput(Number(yearFilter), Number(monthFilter))
      setRangeStart(value)
      setRangeEnd(value)
    } else if (yearFilter !== 'all') {
      setRangeStart(toMonthInput(Number(yearFilter), 1))
      setRangeEnd(toMonthInput(Number(yearFilter), 12))
    } else {
      const value = toMonthInput(CURRENT_YEAR, CURRENT_MONTH)
      setRangeStart(value)
      setRangeEnd(value)
    }
  }

  const isDefaultPeriod = !useCustomRange
    && monthFilter === String(CURRENT_MONTH)
    && yearFilter === String(CURRENT_YEAR)

  const hasActiveFilters = Boolean(
    search
    || statusFilter !== 'all'
    || useCustomRange
    || !isDefaultPeriod
  )

  const cgInvoices = useMemo(
    () => (rowsFromStore || []).filter((inv) => inv.campaignId || inv.coach?.name),
    [rowsFromStore]
  )

  const years = useMemo(() => {
    const set = new Set(cgInvoices.map((inv) => inv.periodYear).filter(Boolean))
    set.add(CURRENT_YEAR)
    return [...set].sort((a, b) => b - a)
  }, [cgInvoices])

  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase()
    const startKey = useCustomRange ? parseMonthValue(rangeStart) : null
    const endKey = useCustomRange ? parseMonthValue(rangeEnd) : null
    const rangeInvalid = useCustomRange && startKey != null && endKey != null && startKey > endKey

    return cgInvoices.filter((inv) => {
      const emailSt = inv.emailStatus || 'pending'
      if (statusFilter !== 'all' && emailSt !== statusFilter) return false

      if (useCustomRange) {
        if (rangeInvalid) return false
        const key = getInvoicePeriodKey(inv)
        if (key == null) return false
        if (startKey != null && key < startKey) return false
        if (endKey != null && key > endKey) return false
      } else {
        if (monthFilter !== 'all' && inv.periodMonth !== Number(monthFilter)) return false
        if (yearFilter !== 'all' && inv.periodYear !== Number(yearFilter)) return false
      }

      if (!q) return true
      const haystack = [
        inv.invoiceNumber,
        inv.coach?.name,
        inv.coach?.email,
        inv.coach?.matriculeFiscal,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [cgInvoices, search, statusFilter, monthFilter, yearFilter, useCustomRange, rangeStart, rangeEnd])

  const stats = useMemo(() => {
    let honoraires = 0
    let net = 0
    let sent = 0
    let pending = 0
    let failed = 0

    filteredInvoices.forEach((inv) => {
      const amounts = getAmounts(inv)
      honoraires += amounts.honoraires
      net += amounts.netAPayer
      const st = inv.emailStatus || 'pending'
      if (st === 'sent') sent++
      else if (st === 'failed') failed++
      else pending++
    })

    return { total: filteredInvoices.length, honoraires, net, sent, pending, failed }
  }, [filteredInvoices])

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const paginatedInvoices = filteredInvoices.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  )

  const yearOnlyFilter = !useCustomRange && monthFilter === 'all' && yearFilter !== 'all'
  const showPagination = filteredInvoices.length > 0 && (
    totalPages > 1 || yearOnlyFilter || useCustomRange
  )
  const rangeInvalid = useCustomRange
    && rangeStart
    && rangeEnd
    && parseMonthValue(rangeStart) > parseMonthValue(rangeEnd)

  const downloadPdf = async (inv) => {
    if (!inv.campaignId) {
      openSnackbar('PDF disponible uniquement pour les factures de campagne')
      return
    }
    setPdfBusyInvoiceId(inv._id)
    try {
      await downloadInvoicePdf(inv.campaignId, inv._id, inv.invoiceNumber)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    } finally {
      setPdfBusyInvoiceId(null)
    }
  }

  const printPdf = async (inv) => {
    if (!inv.campaignId) {
      openSnackbar('PDF disponible uniquement pour les factures de campagne')
      return
    }
    setPdfBusyInvoiceId(inv._id)
    try {
      await printInvoicePdf(inv.campaignId, inv._id)
    } catch {
      openSnackbar('Erreur impression PDF')
    } finally {
      setPdfBusyInvoiceId(null)
    }
  }

  if (!token) return null

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  const emailTotal = stats.sent + stats.pending + stats.failed
  const emailProgress = emailTotal > 0 ? Math.round((stats.sent / emailTotal) * 100) : 0
  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Toutes les factures</h1>
          <p>California Gym — Factures coaches générées</p>
        </div>
        <div className={styles.topBarActions}>
          <RhTemplateMenu onError={openSnackbar} />
          <button className={styles.ctaBtn} onClick={() => history.push('/import')}>
            + Nouvel import mensuel
          </button>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Total factures</span>
          <span className={styles.kpiValue}>{stats.total}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Total honoraires</span>
          <span className={styles.kpiValue}>{formatTND(stats.honoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net à payer</span>
          <span className={styles.kpiValue}>{formatTND(stats.net)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails envoyés</span>
          <span className={styles.kpiValue}>{stats.sent} / {stats.total}</span>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <h2>Statut des envois</h2>
          <div className={styles.emailStats}>
            <div className={styles.emailRow}>
              <span className={styles.dotSent} /> Envoyées
              <strong>{stats.sent}</strong>
            </div>
            <div className={styles.emailRow}>
              <span className={styles.dotPending} /> En attente
              <strong>{stats.pending}</strong>
            </div>
            <div className={styles.emailRow}>
              <span className={styles.dotFailed} /> Échecs
              <strong>{stats.failed}</strong>
            </div>
          </div>
          <div className={styles.progressWrap}>
            <div className={styles.progressBar} style={{ width: `${emailProgress}%` }} />
          </div>
          <p className={styles.progressText}>{emailProgress}% des factures envoyées par email</p>
        </div>

        <div className={styles.panel}>
          <h2>Résumé</h2>
          <ul className={styles.summaryList}>
            <li>
              <span>Factures affichées</span>
              <strong>{filteredInvoices.length}</strong>
            </li>
            <li>
              <span>Client facturé</span>
              <strong>Société Sport et Loisirs</strong>
            </li>
            <li>
              <span>Droit de timbre</span>
              <strong>1 TND / facture</strong>
            </li>
            <li>
              <span>Campagnes</span>
              <button className={styles.linkBtn} onClick={() => history.push('/dashboard')}>
                Voir le dashboard →
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Liste des factures</h2>
          <span className={styles.pageInfo}>{filteredInvoices.length} résultat(s)</span>
        </div>

        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Rechercher par coach, email, N° facture..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Rechercher une facture"
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrer par statut"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className={styles.clearBtn} onClick={resetFilters}>
              Réinitialiser
            </button>
          )}
        </div>

        <div className={styles.periodBar}>
          <label className={styles.periodToggle}>
            <input
              type="checkbox"
              checked={useCustomRange}
              onChange={(e) => (e.target.checked ? enableCustomRange() : setUseCustomRange(false))}
            />
            Période libre
          </label>

          {useCustomRange ? (
            <div className={styles.periodRange}>
              <label className={styles.periodField}>
                <span>Début</span>
                <input
                  type="month"
                  className={styles.monthInput}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  aria-label="Début de période"
                />
              </label>
              <span className={styles.periodArrow} aria-hidden="true">→</span>
              <label className={styles.periodField}>
                <span>Fin</span>
                <input
                  type="month"
                  className={styles.monthInput}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  aria-label="Fin de période"
                />
              </label>
              {rangeInvalid && (
                <span className={styles.periodError}>La date de début doit être avant la fin</span>
              )}
            </div>
          ) : (
            <div className={styles.periodQuick}>
              <select
                className={styles.filterSelect}
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                aria-label="Filtrer par mois"
              >
                <option value="all">Tous les mois</option>
                {MONTHS.slice(1).map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
              <select
                className={styles.filterSelect}
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                aria-label="Filtrer par année"
              >
                <option value="all">Toutes les années</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {rangeInvalid ? (
          <p className={styles.empty}>Période invalide : le mois de début doit être antérieur ou égal au mois de fin.</p>
        ) : filteredInvoices.length === 0 ? (
          <p className={styles.empty}>
            {cgInvoices.length === 0
              ? 'Aucune facture. Lancez un import mensuel pour générer des factures.'
              : 'Aucune facture ne correspond à votre recherche.'}
          </p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Période</th>
                    <th>Coach</th>
                    <th>Email</th>
                    <th>Honoraires</th>
                    <th>Net à payer</th>
                    <th>Statut envoi</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((inv) => {
                    const st = statusLabel[inv.emailStatus || 'pending']
                    const { honoraires, netAPayer } = getAmounts(inv)
                    return (
                      <tr key={inv._id}>
                        <td data-label="N° Facture">{inv.invoiceNumber}</td>
                        <td data-label="Période">{formatPeriod(inv.periodMonth, inv.periodYear)}</td>
                        <td data-label="Coach">{inv.coach?.name || '—'}</td>
                        <td data-label="Email">{inv.coach?.email || '—'}</td>
                        <td data-label="Honoraires">{formatTND(honoraires)} TND</td>
                        <td data-label="Net à payer">{formatTND(netAPayer)} TND</td>
                        <td data-label="Statut envoi">
                          <span className={`${styles.badge} ${styles[st.cls]}`}>{st.text}</span>
                        </td>
                        <td className={styles.actionCell} data-label="Actions">
                          <button
                            type="button"
                            className={styles.pdfBtn}
                            disabled={pdfBusyInvoiceId === inv._id}
                            onClick={() => downloadPdf(inv)}
                          >
                            {pdfBusyInvoiceId === inv._id ? '…' : 'PDF'}
                          </button>
                          <button
                            type="button"
                            className={styles.pdfBtn}
                            disabled={pdfBusyInvoiceId === inv._id}
                            onClick={() => printPdf(inv)}
                          >
                            {pdfBusyInvoiceId === inv._id ? '…' : 'Imprimer'}
                          </button>
                          {inv.campaignId && (
                            <button
                              className={styles.tableBtn}
                              onClick={() => history.push(`/campagnes/${inv.campaignId}`)}
                            >
                              Campagne
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {showPagination && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {safePage} sur {totalPages}
                  {' · '}{filteredInvoices.length} facture(s)
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
                <select
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Nombre de factures par page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Invoices
